import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import ConsultantChatGate from '../components/ConsultantChatGate';
import { trackExpertCatalogClick, trackExpertVendorClick } from '../lib/metrikaGoals';
import QuizForm from '../components/QuizForm';
import CallbackForm from '../components/CallbackForm';
import SituationFilter from '../components/SituationFilter';
import { productsAPI, categoriesAPI, sectionsAPI, vendorsAPI, situationsAPI } from '../lib/api';
import { loadCatalogFacets, loadCatalogProductsPage } from '../lib/catalogGridLoad';
import {
  catalogCategoryPath,
  catalogCategorySectionPath,
  catalogVendorPath,
  catalogSolutionPath,
  CATALOG_SECTION_NONE_SLUG,
  sectionSlugForNavigation,
} from '../lib/catalogRoutes';
import {
  currentCatalogHref,
  inferCatalogParentHref,
  parseCatalogHref,
  resolveCatalogBackTarget,
  withCatalogBack,
} from '../lib/catalogNavigation';
import { plainTextFromHtml, ensureArray } from '../lib/utils';
import VendorCertificateImage from '../components/VendorCertificateImage';
import { solutionsPathForVendorSlug } from '../lib/vendorRoutes';
import {
  normalizeVendorApiProducts,
  shouldShowSolutionsList as shouldShowSolutionsListUtil,
  getCatalogViewLevel,
} from '../lib/catalogVendorProducts';
import SEOHead from '../components/SEOHead';
import ExpandableProse from '../components/ExpandableProse';
import { buildCatalogPageSeo } from '../lib/catalogSeo';
import { ChevronRight, ChevronDown, Store, ArrowLeft, LayoutGrid, Layers } from 'lucide-react';

const CatalogPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState([]);
  const [situations, setSituations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [currentVendor, setCurrentVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [situationFilter, setSituationFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState(null);
  const [productsTotal, setProductsTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [expertContactOpen, setExpertContactOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [rootCatalogView, setRootCatalogView] = useState('vendors');
  const [slugVendorLoading, setSlugVendorLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { vendorSlug: vendorSlugParam, categoryId: categorySlugParam, sectionSlug: sectionSlugParam } = useParams();

  const forwardCatalogState = withCatalogBack(location.state, currentCatalogHref(location));

  const navigateCatalog = (to, { replace = false } = {}) => {
    navigate(to, {
      replace,
      state: forwardCatalogState,
    });
  };

  const catalogContextQuery = () => {
    if (situationFilter) {
      return { situationId: situationFilter };
    }
    return {
      categoryId: categoryFilter !== 'all' ? categoryFilter : undefined,
      sectionName: sectionFilter !== 'all' ? sectionFilter : undefined,
    };
  };

  useEffect(() => {
    if (categorySlugParam) return;
    const categoryParam = searchParams.get('category');
    const sectionParam = searchParams.get('section');
    const vendorParam = searchParams.get('vendor');
    const situationParam = searchParams.get('situation');

    setCategoryFilter(categoryParam || 'all');
    setSectionFilter(sectionParam || 'all');
    setSituationFilter(situationParam || null);

    if (vendorSlugParam) {
      if (currentVendor?.slug === vendorSlugParam && currentVendor?.name) {
        setVendorFilter(currentVendor.name);
      } else if (!vendorParam) {
        setVendorFilter(null);
      }
    } else {
      setVendorFilter(vendorParam || null);
    }
  }, [searchParams, vendorSlugParam, currentVendor?.name, currentVendor?.slug, categorySlugParam]);

  useEffect(() => {
    if (!categorySlugParam) return;
    setVendorFilter(null);
    setCategoryFilter(categorySlugParam);

    if (!sectionSlugParam) {
      setSectionFilter('all');
      return;
    }
    if (sectionSlugParam === CATALOG_SECTION_NONE_SLUG) {
      setSectionFilter('Без раздела');
      return;
    }
    const match = ensureArray(sections).find(
      (s) => s.category_id === categorySlugParam && s.slug === sectionSlugParam,
    );
    setSectionFilter(match?.name || 'all');
  }, [categorySlugParam, sectionSlugParam, sections]);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Stage A: root /solutions loads facets only — no full /api/products.
        const [categoriesData, sectionsData, vendorsData, situationsData] =
          await loadCatalogFacets({
            getCategories: () => categoriesAPI.getCategories(),
            getSections: () => sectionsAPI.getSections(),
            getVendors: (options) => vendorsAPI.getVendors(options),
            getSituations: () => situationsAPI.getSituations(),
          });
        if (cancelled) return;

        const categoriesList = ensureArray(categoriesData);
        const sectionsList = ensureArray(sectionsData);
        const vendorsList = ensureArray(vendorsData);

        setProducts([]);
        setProductsTotal(0);
        const allCategory = categoriesList.find(cat => cat.id === 'all') || { id: 'all', name: 'Все категории' };
        const otherCategories = categoriesList.filter(cat => cat.id !== 'all');
        setCategories([allCategory, ...otherCategories]);
        setSections(sectionsList);
        setVendors(vendorsList);
        setSituations(ensureArray(situationsData));
        setLoading(false);
      } catch (err) {
        console.error('Error loading data:', err);
        if (!cancelled) {
          setError(err.message || 'Ошибка при загрузке данных');
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  // SEO: единый канонический URL — редирект ?vendor=Name → /solutions/:slug (сохраняем category/situation)
  useEffect(() => {
    const vname = searchParams.get('vendor');
    if (!vname || vendors.length === 0 || vendorSlugParam) return;
    const raw = decodeURIComponent(vname).trim();
    const match = ensureArray(vendors).find((x) => (x.name || '').trim().toLowerCase() === raw.toLowerCase());
    if (!match?.slug) return;
    const next = new URLSearchParams(searchParams);
    next.delete('vendor');
    const q = next.toString();
    navigate(`${solutionsPathForVendorSlug(match.slug)}${q ? `?${q}` : ''}`, {
      replace: true,
      state: location.state,
    });
  }, [searchParams, vendors, vendorSlugParam, navigate, location.state]);

  // Убрать дубль ?vendor= при уже выбранном slug в пути
  useEffect(() => {
    if (!vendorSlugParam || !searchParams.get('vendor')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('vendor');
    const q = next.toString();
    navigate(`${solutionsPathForVendorSlug(vendorSlugParam)}${q ? `?${q}` : ''}`, {
      replace: true,
      state: location.state,
    });
  }, [vendorSlugParam, searchParams, navigate, location.state]);

  // Загрузка вендора только по slug из пути /solutions/:slug
  useEffect(() => {
    if (!vendorSlugParam) {
      setSlugVendorLoading(false);
      setCurrentVendor(null);
      return;
    }

    let cancelled = false;
    setSlugVendorLoading(true);
    (async () => {
      try {
        const listMatch = ensureArray(vendors).find((v) => v.slug === vendorSlugParam) || null;
        const vendorData = await vendorsAPI.getVendor(vendorSlugParam).catch(() => null);

        let merged = vendorData
          ? {
              ...(listMatch || {}),
              ...vendorData,
              products: vendorData.products ?? listMatch?.products,
            }
          : listMatch;

        if (!cancelled) {
          setCurrentVendor(merged);
          setSlugVendorLoading(false);
        }
      } catch (err) {
        console.error('Error loading vendor:', err);
        if (!cancelled) {
          setCurrentVendor(null);
          setSlugVendorLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vendorSlugParam, vendors]);

  /** Полный сброс: все вендоры, все категории и ситуации, без slug в URL */
  const resetCatalogToInitial = () => {
    setCategoryFilter('all');
    setSectionFilter('all');
    setSituationFilter(null);
    setVendorFilter(null);
    setSearchQuery('');
    navigate('/solutions', { replace: true });
  };

  /** Один шаг назад: на странице вендора — в категорию/раздел по URL; иначе catalogBack или иерархия. */
  const handleCatalogBack = () => {
    const current = currentCatalogHref(location);
    const target = resolveCatalogBackTarget(
      current,
      location.state,
      ensureArray(sections),
    );
    if (!target || target === current) return;

    const parsed = parseCatalogHref(target, ensureArray(sections));
    setCategoryFilter(parsed.categoryFilter);
    setSectionFilter(parsed.sectionFilter);
    setSituationFilter(parsed.situationFilter);
    if (parsed.clearVendor) {
      setVendorFilter(null);
      setCurrentVendor(null);
    }

    const parent = inferCatalogParentHref(target, ensureArray(sections));
    navigate(target, {
      replace: true,
      state: parent && parent !== target ? withCatalogBack({}, parent) : undefined,
    });
  };

  const handleSituationFilterChange = (situationId) => {
    setSituationFilter(situationId);
    setCategoryFilter('all');
    setSectionFilter('all');
    setVendorFilter(null);
    if (situationId === null) {
      navigateCatalog('/solutions', { replace: true });
      return;
    }
    navigateCatalog(`/solutions?situation=${encodeURIComponent(situationId)}`, { replace: true });
  };

  const canCatalogBack =
    Boolean(vendorSlugParam) ||
    Boolean(currentVendor) ||
    sectionFilter !== 'all' ||
    situationFilter !== null ||
    categoryFilter !== 'all' ||
    Boolean(vendorFilter);

  const slugVendorInvalid = Boolean(vendorSlugParam) && !slugVendorLoading && !currentVendor;

  const productsFromVendorApi = useMemo(
    () => normalizeVendorApiProducts(currentVendor?.products, currentVendor?.name),
    [currentVendor?.products, currentVendor?.name],
  );

  /** Server-paginated catalog page items (Stage B). */
  const filteredProducts = products;

  /** Список решений: не показываем на стартовом /solutions (только карточки вендоров), пока нет фильтра/поиска или страницы вендора */
  const activeNavDepth = useMemo(() => {
    let count = 0;
    if (categoryFilter !== 'all') count += 1;
    if (sectionFilter !== 'all') count += 1;
    if (vendorFilter || vendorSlugParam) count += 1;
    return count;
  }, [categoryFilter, sectionFilter, vendorFilter, vendorSlugParam]);

  const catalogViewLevel = useMemo(
    () => {
      if (situationFilter !== null && !vendorFilter && !vendorSlugParam) {
        return 'vendors';
      }
      return getCatalogViewLevel({
        searchQuery,
        categoryFilter,
        vendorFilter,
        vendorSlugParam,
        sectionFilter,
      });
    },
    [searchQuery, categoryFilter, vendorFilter, vendorSlugParam, sectionFilter, situationFilter],
  );

  const showSolutionsList = useMemo(
    () => {
      if (situationFilter !== null && !vendorFilter && !vendorSlugParam) {
        return false;
      }
      return shouldShowSolutionsListUtil({
        searchQuery,
        categoryFilter,
        situationFilter,
        vendorFilter,
        vendorSlugParam,
        sectionFilter,
      });
    },
    [searchQuery, categoryFilter, sectionFilter, situationFilter, vendorFilter, vendorSlugParam],
  );

  // Stage B: fetch one page when solutions list is visible.
  useEffect(() => {
    if (!showSolutionsList) {
      setProducts([]);
      setProductsTotal(0);
      return undefined;
    }

    let cancelled = false;
    const sectionRecord =
      categoryFilter !== 'all' && sectionFilter !== 'all'
        ? ensureArray(sections).find(
            (s) => s.category_id === categoryFilter && s.name === sectionFilter,
          )
        : null;

    const params = {
      limit: 12,
      offset: 0,
      vendor_slug: vendorSlugParam || undefined,
      category:
        situationFilter !== null
          ? undefined
          : categoryFilter !== 'all'
            ? categoryFilter
            : undefined,
      section_slug:
        situationFilter !== null ? undefined : sectionRecord?.slug || undefined,
      situation_id: situationFilter || undefined,
      q: searchQuery.trim() || undefined,
    };

    setProductsLoading(true);
    loadCatalogProductsPage((opts) => productsAPI.getProducts(opts), params)
      .then((page) => {
        if (cancelled) return;
        const items = Array.isArray(page) ? page : page?.items || [];
        setProducts(items);
        setProductsTotal(Number(page?.total) || items.length);
      })
      .catch((err) => {
        console.error('Error loading catalog products page:', err);
        if (!cancelled) {
          setProducts([]);
          setProductsTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    showSolutionsList,
    vendorSlugParam,
    categoryFilter,
    sectionFilter,
    situationFilter,
    searchQuery,
    sections,
  ]);

  const vendorByNameLower = useMemo(() => {
    const m = new Map();
    ensureArray(vendors).forEach((v) => {
      const key = (v.name || '').trim().toLowerCase();
      if (key) m.set(key, v);
    });
    return m;
  }, [vendors]);

  // Cross-filter auto-reset removed (Stage B): with paginated pages we must not
  // clear filters based on a partial product list — empty grid + total=0 is OK.

  const categoryNavItems = useMemo(() => {
    return ensureArray(categories).filter((cat) => cat.id !== 'all');
  }, [categories]);

  const sectionNavItems = useMemo(() => {
    if (categoryFilter === 'all') return [];
    return ensureArray(sections)
      .filter((s) => s.category_id === categoryFilter)
      .map((s) => s.name)
      .sort((a, b) => a.localeCompare(b, 'ru'));
  }, [categoryFilter, sections]);

  const sectionsByCategory = useMemo(() => {
    const result = new Map();
    categoryNavItems.forEach((cat) => {
      const names = ensureArray(sections)
        .filter((s) => s.category_id === cat.id)
        .map((s) => s.name)
        .sort((a, b) => a.localeCompare(b, 'ru'));
      result.set(cat.id, names);
    });
    return result;
  }, [categoryNavItems, sections]);

  const sectionTiles = useMemo(() => {
    if (catalogViewLevel !== 'sections') return [];
    return sectionNavItems.map((sectionName) => {
      const record = ensureArray(sections).find(
        (s) => s.category_id === categoryFilter && s.name === sectionName,
      );
      const slug = sectionSlugForNavigation(sectionName, record);
      return { name: sectionName, slug, count: null };
    });
  }, [catalogViewLevel, sectionNavItems, sections, categoryFilter]);

  useEffect(() => {
    if (categoryFilter === 'all' && sectionFilter !== 'all') {
      setSectionFilter('all');
      return;
    }
    if (categoryFilter !== 'all' && sectionFilter !== 'all') {
      const exists = sectionNavItems.includes(sectionFilter);
      if (!exists) {
        setSectionFilter('all');
      }
    }
  }, [categoryFilter, sectionFilter, sectionNavItems]);

  useEffect(() => {
    if (categoryFilter !== 'all') {
      setExpandedCategoryId(categoryFilter);
    }
  }, [categoryFilter]);

  const vendorList = useMemo(() => {
    return ensureArray(vendors)
      .map((v) => (v.name || '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'ru'));
  }, [vendors]);

  const gridVendorNames = vendorList;

  const loadMore = () => {
    if (productsLoading || products.length >= productsTotal) return;
    const sectionRecord =
      categoryFilter !== 'all' && sectionFilter !== 'all'
        ? ensureArray(sections).find(
            (s) => s.category_id === categoryFilter && s.name === sectionFilter,
          )
        : null;
    const nextOffset = products.length;
    setProductsLoading(true);
    loadCatalogProductsPage((opts) => productsAPI.getProducts(opts), {
      limit: 12,
      offset: nextOffset,
      vendor_slug: vendorSlugParam || undefined,
      category:
        situationFilter !== null
          ? undefined
          : categoryFilter !== 'all'
            ? categoryFilter
            : undefined,
      section_slug:
        situationFilter !== null ? undefined : sectionRecord?.slug || undefined,
      situation_id: situationFilter || undefined,
      q: searchQuery.trim() || undefined,
    })
      .then((page) => {
        const items = Array.isArray(page) ? page : page?.items || [];
        setProducts((prev) => [...prev, ...items]);
        setProductsTotal(Number(page?.total) || productsTotal);
      })
      .catch((err) => console.error('Error loading more products:', err))
      .finally(() => setProductsLoading(false));
  };

  const rootVendorNames = useMemo(() => {
    if (catalogViewLevel !== 'root') return [];
    return vendorList;
  }, [catalogViewLevel, vendorList]);

  const selectedSectionMeta = useMemo(() => {
    if (categoryFilter === 'all' || sectionFilter === 'all') return null;
    return ensureArray(sections).find(
      (s) => s.category_id === categoryFilter && s.name === sectionFilter,
    ) || null;
  }, [sections, categoryFilter, sectionFilter]);

  const selectedCategoryMeta = useMemo(() => {
    if (categoryFilter === 'all') return null;
    return ensureArray(categories).find((c) => c.id === categoryFilter) || null;
  }, [categories, categoryFilter]);

  const catalogPageSeo = useMemo(
    () =>
      buildCatalogPageSeo({
        vendor: currentVendor,
        vendorProducts: productsFromVendorApi,
        category: selectedCategoryMeta,
        section: selectedSectionMeta,
        pathname: location.pathname,
      }),
    [
      currentVendor,
      productsFromVendorApi,
      selectedCategoryMeta,
      selectedSectionMeta,
      location.pathname,
    ],
  );

  const sectionDescriptionHtml = useMemo(() => {
    const raw = selectedSectionMeta?.description || '';
    if (!raw) return '';
    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(raw);
    if (hasHtmlTags) return raw;
    // Preserve user-entered line breaks/spaces when description is plain text.
    return raw
      .split('\n')
      .map((line) => line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
      .join('<br/>');
  }, [selectedSectionMeta?.description]);

  const selectedVendorMeta = useMemo(() => {
    if (catalogViewLevel !== 'vendor') return null;
    if (currentVendor) return currentVendor;
    if (!vendorFilter) return null;
    return ensureArray(vendors).find((v) => v.name === vendorFilter) || null;
  }, [catalogViewLevel, currentVendor, vendorFilter, vendors]);

  const vendorDescriptionHtml = useMemo(() => {
    const raw =
      selectedVendorMeta?.full_description?.trim() ||
      selectedVendorMeta?.description?.trim() ||
      '';
    if (!raw || !plainTextFromHtml(raw, 1)) return '';
    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(raw);
    if (hasHtmlTags) return raw;
    return raw
      .split('\n')
      .map((line) => line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
      .join('<br/>');
  }, [selectedVendorMeta?.full_description, selectedVendorMeta?.description]);

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title={catalogPageSeo.title}
        description={catalogPageSeo.description}
        keywords={catalogPageSeo.keywords}
        canonical={catalogPageSeo.canonical}
      />
      <Header />

      <main className="flex-1" style={{ paddingTop: '5rem' }}>
        {/* Breadcrumbs */}
        <div className="max-w-screen-2xl mx-auto px-3 sm:px-5 lg:px-6 xl:px-8 pb-2">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Главная</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/solutions" className="hover:text-foreground transition-colors font-medium">Каталог решений</Link>
            {categoryFilter !== 'all' && (
              <>
                <ChevronRight className="h-4 w-4" />
                <button
                  type="button"
                  onClick={() => {
                    setCategoryFilter('all');
                    setSectionFilter('all');
                    setVendorFilter(null);
                    navigateCatalog('/solutions', { replace: true });
                  }}
                  className="hover:text-foreground transition-colors font-medium"
                >
                  {(() => {
                    const cat = ensureArray(categories).find(c => c.id === categoryFilter);
                    return cat?.name || categoryFilter;
                  })()}
                </button>
              </>
            )}
            {sectionFilter !== 'all' && (
              <>
                <ChevronRight className="h-4 w-4" />
                <button
                  onClick={() => {
                    setSectionFilter('all');
                    if (categoryFilter !== 'all') {
                      navigateCatalog(catalogCategoryPath(categoryFilter), { replace: true });
                    }
                  }}
                  className="hover:text-foreground transition-colors font-medium"
                >
                  {sectionFilter}
                </button>
              </>
            )}
            {vendorFilter && (
              <>
                <ChevronRight className="h-4 w-4" />
                <button 
                  onClick={() => setVendorFilter(null)}
                  className="hover:text-foreground transition-colors font-medium"
                >
                  {vendorFilter}
                </button>
              </>
            )}
            {situationFilter && (
              <>
                <ChevronRight className="h-4 w-4" />
                <button 
                  onClick={() => setSituationFilter(null)}
                  className="hover:text-foreground transition-colors font-medium"
                >
                  {situationFilter}
                </button>
              </>
            )}
          </nav>
        </div>

        <section className="max-w-screen-2xl mx-auto px-3 sm:px-5 lg:px-6 xl:px-8 pt-2 pb-12">

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-80 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <button
                  type="button"
                  className="lg:hidden w-full flex items-center justify-between gap-3 px-4 py-3 mb-2 rounded-xl border border-border bg-white text-sm font-medium shadow-sm"
                  onClick={() => setMobileNavOpen((open) => !open)}
                  aria-expanded={mobileNavOpen}
                  aria-controls="catalog-nav-panel"
                >
                  <span className="flex items-center gap-2">
                    <LayoutGrid className="w-4 h-4 text-muted-foreground" aria-hidden />
                    Навигация каталога
                    {activeNavDepth > 0 && (
                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-xs font-semibold text-accent-foreground">
                        {activeNavDepth}
                      </span>
                    )}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${
                      mobileNavOpen ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                  />
                </button>

                <div
                  id="catalog-nav-panel"
                  className={`${mobileNavOpen ? 'block' : 'hidden'} lg:block`}
                >
                  <div className="bg-white border border-border/50 rounded-xl p-4 space-y-4">
                    <div>
                      <h3 className="font-semibold mb-2">Категории</h3>
                      <div className="space-y-2">
                        {categoryNavItems.map((cat) => {
                          const isExpanded = expandedCategoryId === cat.id;
                          const isActiveCategory = categoryFilter === cat.id;
                          const nestedSections = sectionsByCategory.get(cat.id) || [];
                          return (
                            <div key={cat.id} className="rounded-lg border border-border/50 bg-white">
                              <div className="flex items-center">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCategoryFilter(cat.id);
                                    setSectionFilter('all');
                                    setVendorFilter(null);
                                    navigateCatalog(catalogCategoryPath(cat.id), { replace: true });
                                  }}
                                  className="group flex-1 text-left px-3 py-2 rounded-l-lg text-sm"
                                >
                                  <span
                                    className={`inline-block border-b-2 transition-colors ${
                                      isActiveCategory
                                        ? 'font-semibold border-current'
                                        : 'border-transparent group-hover:border-current'
                                    }`}
                                  >
                                    {cat.name}
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedCategoryId((prev) => (prev === cat.id ? null : cat.id))
                                  }
                                  className="px-2 py-2 rounded-r-lg text-muted-foreground cursor-pointer"
                                  aria-label={`Показать разделы категории ${cat.name}`}
                                >
                                  <ChevronDown
                                    className={`w-4 h-4 transition-transform ${
                                      isExpanded ? 'rotate-180' : ''
                                    }`}
                                  />
                                </button>
                              </div>
                              {isExpanded && (
                                <div className="px-2 pb-2 space-y-1">
                                  {nestedSections.map((sectionName) => (
                                    <button
                                      key={`${cat.id}-${sectionName}`}
                                      type="button"
                                      onClick={() => {
                                        setCategoryFilter(cat.id);
                                        setSectionFilter(sectionName);
                                        setVendorFilter(null);
                                        const sectionRecord = ensureArray(sections).find(
                                          (s) => s.category_id === cat.id && s.name === sectionName,
                                        );
                                        const slug = sectionSlugForNavigation(sectionName, sectionRecord);
                                        navigateCatalog(catalogCategorySectionPath(cat.id, slug), {
                                          replace: true,
                                        });
                                      }}
                                      className="group w-full text-left px-3 py-2 rounded-lg text-sm"
                                    >
                                      <span
                                        className={`inline-block border-b-2 transition-colors ${
                                          isActiveCategory && sectionFilter === sectionName
                                            ? 'font-medium border-current'
                                            : 'border-transparent group-hover:border-current'
                                        }`}
                                      >
                                        {sectionName}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="hidden lg:block">
                      <SituationFilter
                        situations={ensureArray(situations)}
                        activeFilter={situationFilter}
                        onFilterChange={handleSituationFilterChange}
                      />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full mt-4"
                    onClick={resetCatalogToInitial}
                  >
                    Сбросить
                  </Button>
                </div>
              </div>
            </aside>

            <div className="flex-1">
              {canCatalogBack && (
                <div className="mb-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-2 h-9 px-2 text-muted-foreground hover:text-foreground"
                    onClick={handleCatalogBack}
                  >
                    <ArrowLeft className="w-4 h-4 shrink-0" />
                    Назад
                  </Button>
                </div>
              )}
              {loading && (
                <div className="text-center py-16">
                  <p className="text-muted-foreground">Загрузка решений...</p>
                </div>
              )}

              {error && !loading && (
                <div className="text-center py-16">
                  <p className="text-destructive mb-4">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="text-accent hover:underline"
                  >
                    Перезагрузить страницу
                  </button>
                </div>
              )}

              {!loading && !error && (
                <>
                  {slugVendorLoading && vendorSlugParam && !currentVendor ? (
                    <div className="text-center py-16 text-muted-foreground">Загрузка вендора…</div>
                  ) : slugVendorInvalid ? (
                    <div className="text-center py-16 space-y-4">
                      <p className="text-muted-foreground">Вендор по этой ссылке не найден.</p>
                      <Button variant="outline" asChild>
                        <Link to="/solutions">В каталог решений</Link>
                      </Button>
                    </div>
                  ) : (
                    <>
                  {catalogViewLevel === 'root' && (
                    <>
                      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-foreground">
                        {catalogPageSeo.h1 ||
                          'Каталог лицензий на российское и зарубежное ПО и AI'}
                      </h1>
                      <div className="mb-4 border-b border-border/70">
                        <div className="flex gap-4 text-sm">
                          <button
                            type="button"
                            onClick={() => setRootCatalogView('vendors')}
                            className={`pb-2 border-b-2 transition-colors ${
                              rootCatalogView === 'vendors'
                                ? 'border-accent font-semibold'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            Вендоры
                          </button>
                          <button
                            type="button"
                            onClick={() => setRootCatalogView('categories')}
                            className={`pb-2 border-b-2 transition-colors ${
                              rootCatalogView === 'categories'
                                ? 'border-accent font-semibold'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            Категории
                          </button>
                        </div>
                      </div>

                      {rootCatalogView === 'vendors' ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8 mb-8">
                          {rootVendorNames.map((vendorName) => {
                            const vendor = ensureArray(vendors).find((v) => v.name === vendorName);
                            const vendorBlurb =
                              vendor?.description?.trim() ||
                              plainTextFromHtml(vendor?.full_description, 200) ||
                              '';
                            const vendorHref = vendor?.slug
                              ? catalogVendorPath(vendor.slug, catalogContextQuery())
                              : `/solutions?vendor=${encodeURIComponent(vendorName)}`;
                            return (
                              <Link
                                key={vendorName}
                                to={vendorHref}
                                state={forwardCatalogState}
                                className="block cursor-pointer p-8 lg:p-10 min-h-[22rem] bg-white border border-border/50 rounded-xl hover:border-accent/50 hover:shadow-lg transition-all duration-300"
                              >
                                <div className="flex h-full flex-col items-center justify-center gap-5 lg:gap-6 text-center">
                                  <div className="w-full max-w-[13rem] sm:max-w-[15rem] lg:max-w-[17rem] aspect-square rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden p-5 lg:p-6">
                                    {vendor?.logo ? (
                                      <img src={vendor.logo} alt={vendor.name} className="w-full h-full object-contain" />
                                    ) : (
                                      <Store className="w-20 h-20 lg:w-24 lg:h-24 text-accent" />
                                    )}
                                  </div>
                                  <div>
                                    <h3 className="text-xl lg:text-2xl font-semibold mb-2">{vendorName}</h3>
                                    {vendorBlurb ? (
                                      <p className="text-sm lg:text-base text-muted-foreground line-clamp-2">
                                        {vendorBlurb}
                                      </p>
                                    ) : null}
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                          {categoryNavItems.map((cat) => (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => {
                                setCategoryFilter(cat.id);
                                setSectionFilter('all');
                                setVendorFilter(null);
                                navigateCatalog(catalogCategoryPath(cat.id), { replace: true });
                              }}
                              className="text-center p-6 bg-white border border-border/50 rounded-xl hover:border-accent/50 hover:shadow-lg transition-all duration-300"
                            >
                              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4 mx-auto">
                                <LayoutGrid className="w-6 h-6 text-accent" />
                              </div>
                              <h3 className="text-lg font-semibold mb-1">{cat.name}</h3>
                              <p className="text-sm text-muted-foreground">Разделы и решения категории</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {catalogViewLevel === 'sections' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                      {sectionTiles.map((section) => (
                        <button
                          key={section.name}
                          type="button"
                          onClick={() => {
                            setSectionFilter(section.name);
                            setVendorFilter(null);
                            navigateCatalog(catalogCategorySectionPath(categoryFilter, section.slug), {
                              replace: true,
                            });
                          }}
                          className="text-left p-6 bg-white border border-border/50 rounded-xl hover:border-accent/50 hover:shadow-lg transition-all duration-300"
                        >
                          <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                            <Layers className="w-6 h-6 text-accent" />
                          </div>
                          <h3 className="text-lg font-semibold mb-1">{section.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {section.count == null
                              ? 'Открыть раздел'
                              : `${section.count} ${section.count === 1 ? 'решение' : 'решений'}`}
                          </p>
                        </button>
                      ))}
                      {sectionTiles.length === 0 && (
                        <p className="text-muted-foreground col-span-full py-8 text-center">
                          В этой категории пока нет разделов
                        </p>
                      )}
                    </div>
                  )}

                  {catalogViewLevel === 'vendors' && sectionDescriptionHtml && (
                    <div className="bg-white border border-border/50 rounded-xl p-6 mt-4 mb-8">
                      <h2 className="text-xl font-semibold mb-4">{sectionFilter}</h2>
                      <ExpandableProse html={sectionDescriptionHtml} lines={5} />

                      <div className="mt-6 pt-6 border-t border-border">
                        <h3 className="text-lg font-bold text-foreground mb-4">
                          Запишитесь на консультацию прямо сейчас!
                        </h3>
                        <div className="flex flex-wrap items-center gap-4">
                          <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-accent/20">
                            <img
                              src="/images/founder.png"
                              alt="Сергей Успенский"
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="font-semibold">Сергей Успенский</p>
                            <p className="text-sm text-muted-foreground">Эксперт по IT-решениям</p>
                          </div>
                          <Button
                            onClick={() => {
                              trackExpertCatalogClick();
                              setExpertContactOpen(true);
                            }}
                          >
                            Связаться
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {catalogViewLevel === 'vendors' && gridVendorNames.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8 mb-8">
                      {gridVendorNames.map((vendorName) => {
                        const vendor = ensureArray(vendors).find((v) => v.name === vendorName);
                        const vendorBlurb =
                          vendor?.description?.trim() ||
                          plainTextFromHtml(vendor?.full_description, 200) ||
                          '';
                        const vendorHref = vendor?.slug
                          ? catalogVendorPath(vendor.slug, catalogContextQuery())
                          : `/solutions?vendor=${encodeURIComponent(vendorName)}`;
                        return (
                          <Link
                            key={vendorName}
                            to={vendorHref}
                            state={forwardCatalogState}
                            className="block cursor-pointer p-8 lg:p-10 min-h-[22rem] bg-white border border-border/50 rounded-xl hover:border-accent/50 hover:shadow-lg transition-all duration-300"
                          >
                            <div className="flex h-full flex-col items-center justify-center gap-5 lg:gap-6 text-center">
                              <div className="w-full max-w-[13rem] sm:max-w-[15rem] lg:max-w-[17rem] aspect-square rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden p-5 lg:p-6">
                                {vendor?.logo ? (
                                  <img src={vendor.logo} alt={vendor.name} className="w-full h-full object-contain" />
                                ) : (
                                  <Store className="w-20 h-20 lg:w-24 lg:h-24 text-accent" />
                                )}
                              </div>
                              <div>
                                <h3 className="text-xl lg:text-2xl font-semibold mb-2">{vendorName}</h3>
                                {vendorBlurb ? (
                                  <p className="text-sm lg:text-base text-muted-foreground line-clamp-2">
                                    {vendorBlurb}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}

                  {catalogViewLevel === 'vendors' && gridVendorNames.length === 0 && (
                    <p className="text-muted-foreground py-8 text-center">
                      В этом разделе пока нет вендоров
                    </p>
                  )}

                  {/* Страница конкретного вендора */}
                  {catalogViewLevel === 'vendor' && vendorSlugParam && (currentVendor || vendorFilter) && (
                    <div className="mb-8">
                      {(() => {
                        const vendorInfo =
                          currentVendor ||
                          ensureArray(vendors).find((v) => v.name === vendorFilter) || {
                            name: vendorFilter,
                          };
                        return (
                      <>
                      <div className="bg-white border border-border/50 rounded-xl p-6 mb-8">
                        <div className="flex flex-col gap-4">
                          <div className="w-48 h-48 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden p-6">
                            {vendorInfo.logo ? (
                              <img src={vendorInfo.logo} alt={vendorInfo.name} className="w-full h-full object-contain" />
                            ) : (
                              <Store className="w-24 h-24 text-accent" />
                            )}
                          </div>
                          <div>
                            <h1 className="text-2xl font-bold">
                              {catalogPageSeo.h1 || vendorInfo.name}
                            </h1>
                          </div>
                        </div>

                        {vendorDescriptionHtml && (
                          <div className="mt-6">
                            <ExpandableProse
                              html={vendorDescriptionHtml}
                              lines={5}
                              collapseOnMobileOnly
                            />
                          </div>
                        )}
                        
                        {/* Блок "Обсудить задачу с экспертом" */}
                        <div className="mt-6 pt-6 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                          <div className="min-w-0 flex-1">
                            <h2 className="text-lg font-bold text-foreground mb-4">
                              Запишитесь на консультацию прямо сейчас!
                            </h2>
                            <div className="flex flex-wrap items-center gap-4">
                              <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-accent/20">
                                <img
                                  src="/images/founder.png"
                                  alt="Сергей Успенский"
                                  className="h-full w-full object-cover"
                                />
                              </div>
                              <div>
                                <p className="font-semibold">Сергей Успенский</p>
                                <p className="text-sm text-muted-foreground">Эксперт по IT-решениям</p>
                              </div>
                              <Button
                                onClick={() => {
                                  trackExpertVendorClick();
                                  setExpertContactOpen(true);
                                }}
                              >
                                Связаться
                              </Button>
                            </div>
                          </div>
                          <VendorCertificateImage vendor={vendorInfo} className="w-40 h-44 md:w-48 md:h-52" />
                        </div>
                      </div>
                      
                      {/* Хлебные крошки */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                        <Link to="/solutions" className="hover:text-foreground">Каталог</Link>
                        <ChevronRight className="w-4 h-4" />
                        <span className="text-foreground font-medium">{vendorInfo.name}</span>
                      </div>
                      </>
                        );
                      })()}
                    </div>
                  )}

                  {showSolutionsList && (
                  <>
                  {/* Решения: категория/ситуация/поиск, страница вендора и т.п. */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredProducts.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        centerContent={catalogViewLevel === 'vendor'}
                        linkTo={(() => {
                          if (product.slug) return `/landing/${product.slug}`;
                          if (product.link) return product.link;
                          const vendorKey =
                            typeof product.vendor === 'string'
                              ? product.vendor.trim().toLowerCase()
                              : '';
                          const vendorSlug = vendorKey
                            ? vendorByNameLower.get(vendorKey)?.slug
                            : undefined;
                          if (!vendorSlug || !product.id) return undefined;
                          return catalogSolutionPath(vendorSlug, product.id, catalogContextQuery());
                        })()}
                        linkState={forwardCatalogState}
                        vendorCatalogSlug={
                          typeof product.vendor === 'string'
                            ? vendorByNameLower.get(product.vendor.trim().toLowerCase())?.slug
                            : undefined
                        }
                      />
                    ))}
                  </div>

                  {products.length < productsTotal && (
                    <div className="flex justify-center mt-8">
                      <Button onClick={loadMore} variant="outline" disabled={productsLoading}>
                        {productsLoading ? 'Загрузка…' : 'Показать ещё'}
                      </Button>
                    </div>
                  )}

                  {filteredProducts.length === 0 && (
                    <div className="text-center py-16">
                      <p className="text-muted-foreground mb-4">Решения не найдены</p>
                      <button
                        type="button"
                        onClick={resetCatalogToInitial}
                        className="text-accent hover:underline"
                      >
                        Сбросить навигацию
                      </button>
                    </div>
                  )}
                  </>
                  )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ConsultantChatGate />
      <QuizForm open={quizOpen} onOpenChange={setQuizOpen} />
      <CallbackForm
        open={expertContactOpen}
        onOpenChange={setExpertContactOpen}
        title="Свяжитесь со мной"
        description="Оставьте номер телефона или email — ответим и предложим время для консультации."
        submitLabel="Отправить"
        requireInn
      />
    </div>
  );
};

export default CatalogPage;